import { cn } from "@tokenterminal/core/utils/cn"
import { type HTMLAttributes, useCallback, useRef } from "react"

export type ScrollFadeProps = HTMLAttributes<HTMLDivElement> & {
  /** Scroll direction to track. Defaults to "x". */
  axis?: "x" | "y"
  /** Size of the fade gradient in px. Defaults to 32. */
  fadeSize?: number
}

/**
 * Scrollable container that fades its edges while it can be scrolled further
 * in that direction, hinting that there is more content. Each fade collapses
 * as that end of the scroll range is reached, and no fade is shown at all
 * when the content fits.
 *
 * ```tsx
 * import { ScrollFade } from "@tokenterminal/core/ui/layout/scroll-fade/ScrollFade"
 *
 * <ScrollFade>{scrollable content}</ScrollFade>
 * ```
 *
 * Scrolling can be limited to a breakpoint via `className` (overrides win
 * over the built-in `overflow-x-auto` / `overflow-y-auto`), e.g.
 * `md:overflow-x-visible` for a container that should only scroll on mobile.
 *
 * The distance to each scroll edge (capped at `fadeSize`) is written into the
 * `--tt-scroll-fade-start` / `--tt-scroll-fade-end` custom properties, which
 * ScrollFade.css uses as `mask-image` gradient stops — so the fade tracks the
 * scroll position exactly and collapses smoothly at the ends, no transitions
 * needed. When the element does not overflow, no mask is applied at all.
 *
 * Deliberately JS-driven: a pure-CSS version using scroll-driven animations
 * (`animation-timeline: scroll(self)`) was tried and reverted — the
 * animations intermittently froze at their initial values when created
 * during real page load, leaving a fade stuck at the scroll end.
 */
export const ScrollFade = ({
  axis = "x",
  children,
  className,
  fadeSize = 32,
  ...props
}: ScrollFadeProps) => {
  const cleanupRef = useRef<(() => void) | null>(null)

  const scrollFadeRef = useCallback(
    (element: HTMLDivElement | null) => {
      cleanupRef.current?.()
      cleanupRef.current = null
      if (!element) {
        return
      }

      // Scrolling can't change scroll-container status, so the scroll
      // listener only updates the fade sizes; the getComputedStyle overflow
      // check runs on the (rarer) events that can change it.
      const updateFades = () => {
        const scrolled = Math.abs(
          axis === "x" ? element.scrollLeft : element.scrollTop
        )
        const maxScroll =
          axis === "x"
            ? element.scrollWidth - element.clientWidth
            : element.scrollHeight - element.clientHeight
        element.style.setProperty(
          "--tt-scroll-fade-start",
          `${Math.min(Math.max(scrolled, 0), fadeSize)}px`
        )
        element.style.setProperty(
          "--tt-scroll-fade-end",
          `${Math.min(Math.max(maxScroll - scrolled, 0), fadeSize)}px`
        )
        return maxScroll
      }

      const update = () => {
        const maxScroll = updateFades()
        // Content can also overflow a non-scroll container (overflow:
        // visible), e.g. above a breakpoint that disables scrolling — a fade
        // there would hint at a scroll that isn't possible.
        const overflow =
          getComputedStyle(element)[axis === "x" ? "overflowX" : "overflowY"]
        const isScrollContainer = overflow === "auto" || overflow === "scroll"

        // > 1 rather than > 0 to ignore subpixel rounding on zoomed displays.
        if (isScrollContainer && maxScroll > 1) {
          element.setAttribute("data-scroll-fade", "")
        } else {
          element.removeAttribute("data-scroll-fade")
        }
      }

      update()
      element.addEventListener("scroll", updateFades, { passive: true })
      // A breakpoint (e.g. md:overflow-x-visible) can flip scroll-container
      // status without changing the element's box size, which no observer
      // below reports — media queries only flip on window resize, so this
      // listener covers it.
      window.addEventListener("resize", update, { passive: true })

      // Overflow changes without a scroll event when the container or its
      // children resize, or when children are added/removed.
      const observeAll = () => {
        resizeObserver.disconnect()
        resizeObserver.observe(element)
        for (const child of element.children) {
          resizeObserver.observe(child)
        }
      }
      const resizeObserver = new ResizeObserver(update)
      observeAll()
      const mutationObserver = new MutationObserver(() => {
        observeAll()
        update()
      })
      mutationObserver.observe(element, { childList: true })

      cleanupRef.current = () => {
        element.removeEventListener("scroll", updateFades)
        window.removeEventListener("resize", update)
        resizeObserver.disconnect()
        mutationObserver.disconnect()
      }
    },
    [axis, fadeSize]
  )

  return (
    <div
      ref={scrollFadeRef}
      className={cn(
        axis === "x"
          ? "tt-scroll-fade-x overflow-x-auto"
          : "tt-scroll-fade-y overflow-y-auto",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}
